import { heatRanking } from "@/mocks/home";

const trendMeta: Record<string, { icon: string; color: string }> = {
  up: { icon: "ri-arrow-up-line", color: "text-primary-600" },
  down: { icon: "ri-arrow-down-line", color: "text-secondary-500" },
  flat: { icon: "ri-subtract-line", color: "text-foreground-400" },
};

export default function HeatRanking() {
  const [top, ...rest] = heatRanking;

  return (
    <section id="ranking" className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto w-full max-w-site px-4 md:px-8">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.24em] text-primary-600">
              <i className="ri-fire-line text-sm"></i>
              HEAT RANKING
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              实时热度榜
            </h2>
          </div>
          <p className="max-w-md text-sm leading-7 text-foreground-600">
            按讨论增速、信源扩散与关联搜索综合排序，热度越高越值得优先拆解完整脉络。
          </p>
        </div>

        <div className="mt-9 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="flex flex-col gap-4 lg:col-span-8">
            <article className="rounded-lg border border-primary-200 bg-primary-50/60 p-5 md:p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <span className="font-heading text-4xl font-black leading-none text-primary-500 md:text-5xl">
                    01
                  </span>
                  <div>
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-2.5 py-0.5 text-[11px] font-medium text-background-50">
                      <i className="ri-fire-line"></i>
                      榜首
                    </span>
                    <h3 className="mt-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                      {top.title}
                    </h3>
                  </div>
                </div>
                <span className="hidden whitespace-nowrap rounded-md border border-primary-200 bg-background-50 px-3 py-1 text-xs text-primary-700 sm:inline-flex">
                  {top.category}
                </span>
              </div>

              <p className="mt-4 text-sm leading-7 text-foreground-700">{top.summary}</p>

              <div className="mt-5 flex items-center gap-4">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-primary-100">
                  <div
                    className="animate-grow-x h-full rounded-full bg-primary-500"
                    style={{ width: `${top.heat}%` }}
                  ></div>
                </div>
                <span className="flex items-center gap-1 whitespace-nowrap font-heading text-lg font-bold text-primary-600">
                  {top.heat}
                  <i className={`${trendMeta[top.trend].icon} text-base`}></i>
                </span>
              </div>
            </article>

            <div className="divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {rest.map((item) => (
                <div
                  key={item.rank}
                  className="flex flex-col gap-3 p-4 transition-colors hover:bg-background-100/70 md:flex-row md:items-center md:gap-5 md:p-5"
                >
                  <span className="font-heading text-2xl font-bold text-foreground-300 md:w-10 md:text-3xl">
                    {String(item.rank).padStart(2, "0")}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-heading text-base font-semibold text-foreground-950">
                        {item.title}
                      </h4>
                      <span className="whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] text-secondary-800">
                        {item.category}
                      </span>
                    </div>
                    <p className="mt-1.5 line-clamp-2 text-xs leading-6 text-foreground-600 md:text-sm">
                      {item.summary}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 md:w-40 md:justify-end">
                    <div className="h-1.5 w-24 overflow-hidden rounded-full bg-background-200">
                      <div
                        className="animate-grow-x h-full rounded-full bg-accent-500"
                        style={{ width: `${item.heat}%` }}
                      ></div>
                    </div>
                    <span className="w-10 text-right font-heading text-sm font-bold text-foreground-800">
                      {item.heat}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <aside className="flex flex-col gap-4 lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-bar-chart-2-line text-primary-600"></i>
                热度构成
              </h3>
              <ul className="mt-5 flex flex-col gap-4">
                {[
                  { label: "讨论增速", value: 86, tone: "bg-primary-500" },
                  { label: "信源扩散", value: 72, tone: "bg-accent-500" },
                  { label: "关联搜索", value: 64, tone: "bg-secondary-500" },
                  { label: "存续时长", value: 48, tone: "bg-foreground-400" },
                ].map((row) => (
                  <li key={row.label}>
                    <div className="flex items-center justify-between text-xs text-foreground-600">
                      <span>{row.label}</span>
                      <span className="font-medium text-foreground-800">{row.value}</span>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-background-200">
                      <div
                        className={`animate-grow-x h-full rounded-full ${row.tone}`}
                        style={{ width: `${row.value}%` }}
                      ></div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-rocket-2-line text-accent-600"></i>
                今日飙升
              </h3>
              <ul className="mt-4 flex flex-col gap-3">
                {heatRanking.slice(0, 4).map((item) => (
                  <li key={item.rank} className="flex items-center justify-between gap-3">
                    <a
                      href="#reports"
                      className="cursor-pointer truncate text-sm text-foreground-700 transition-colors hover:text-primary-600"
                    >
                      {item.title}
                    </a>
                    <span
                      className={`whitespace-nowrap font-heading text-sm font-bold ${trendMeta[item.trend].color}`}
                    >
                      {item.delta > 0 ? `+${item.delta}` : item.delta}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}